/**
 * TR-01 / TR-02 lexical-preference grams (T2-DESIGN §4.2, DESIGN CD-46). ONE implementation used by the engine channels and by the
 * mining data dump (`tools/t2/dump-grams.ts`), so the marker rates the trainer mines on are, by construction, the rates the
 * engine computes (exact train/serve parity, T-EVAL-10). Pure: no Node imports, no state.
 *
 * Specification (the exact text the lexicon was mined on):
 *   - Input is ONE paragraph's text (`doc.text.slice(block.charStart, block.charEnd)` of a prose paragraph).
 *   - TOKEN  = a run of Unicode letters (\p{L}, then any \p{L}/\p{M}) with INTERNAL apostrophes: `don't`, `o'er`. The apostrophe
 *     characters ' ’ ‘ are the same character (folded to '). A leading or trailing apostrophe is not part of the token
 *     (`'tis` -> `tis`, `dogs’` -> `dogs`). Digits, hyphens, dashes and every other character end a token and are not tokens:
 *     `to-day` is the two tokens `to`, `day`; `1914` is no token.
 *   - FORM   = the token lower-cased, with ’ ‘ folded to '. Offsets (start, end) are UTF-16 offsets into the paragraph text.
 *   - UNIGRAM = a token form. BIGRAM = "form1 form2" for two ADJACENT tokens whose gap is whitespace alone (one or more
 *     whitespace characters, nothing else): `well, said` forms no bigram (comma), `to-day` forms none (hyphen), `in 1914 the`
 *     forms none (a number between).
 *   - COUNT  = occurrences in the paragraph. The paragraph contribution compresses each gram's count with log1p (CD-51):
 *     K_p = Σ_g weight(g) × log1p(count_p(g)); the document rate is 1000 × Σ_p K_p / Σ_p E_p (E_p = the engine's own
 *     words of the paragraph). So a document's gram sum is L_g = Σ_p log1p(c_{g,p}), which `tools/t2/dump-grams.ts` stores and
 *     the lexicon was fitted on: rate = 1000 × Σ_g w_g L_g / E, exactly the channel's K/E.
 *   - EXCLUSIONS (`isExcludedGram`): a gram is excluded when any of its tokens is in `tr-exclusions.json#tokens` or the whole
 *     gram is in `#grams`. Excluded grams never enter a lexicon.
 */
import exclusionData from './v3/tr-exclusions.json' with { type: 'json' };

// Letters, then letters/marks, with internal apostrophes (each followed by a letter). ’ U+2019 and ‘ U+2018 are apostrophes.
const TOKEN = /\p{L}[\p{L}\p{M}]*(?:['’‘]\p{L}[\p{L}\p{M}]*)*/gu;
const FOLD = /[’‘]/g;
const WS = /^\s+$/;

export interface TrTokens {
	/** Lower-cased, apostrophe-folded token forms. */
	forms: string[];
	/** UTF-16 start offset of token i in the paragraph text. */
	starts: number[];
	/** UTF-16 end offset (exclusive) of token i. */
	ends: number[];
	/** adj[i] = 1 when tokens i-1 and i are separated by whitespace alone (a bigram is allowed); adj[0] = 0. */
	adj: Uint8Array;
}

/** Tokens of one paragraph, with offsets and the whitespace-adjacency flags that decide bigrams. */
export function trTokenise(paragraphText: string): TrTokens {
	const forms: string[] = [];
	const starts: number[] = [];
	const ends: number[] = [];
	const adjList: number[] = [];
	TOKEN.lastIndex = 0;
	let prevEnd = -1;
	for (let m = TOKEN.exec(paragraphText); m; m = TOKEN.exec(paragraphText)) {
		const s = m.index;
		const e = s + m[0].length;
		const raw = m[0];
		forms.push(raw.replace(FOLD, "'").toLowerCase());
		starts.push(s);
		ends.push(e);
		let adj = 0;
		if (prevEnd >= 0 && s > prevEnd) {
			const gap = paragraphText.slice(prevEnd, s);
			adj = gap === ' ' || WS.test(gap) ? 1 : 0;
		}
		adjList.push(adj);
		prevEnd = e;
	}
	return { forms, starts, ends, adj: Uint8Array.from(adjList) };
}

/** The lower-cased token forms of a paragraph (spec above). */
export function trTokens(paragraphText: string): string[] {
	return trTokenise(paragraphText).forms;
}

/** Unigram and bigram counts of one paragraph (the channel applies log1p to each count at scoring). */
export function trGrams(paragraphText: string): Map<string, number> {
	const t = trTokenise(paragraphText);
	const out = new Map<string, number>();
	const f = t.forms;
	for (let i = 0; i < f.length; i++) {
		const a = f[i] as string;
		out.set(a, (out.get(a) ?? 0) + 1);
		if (i > 0 && t.adj[i] === 1) {
			const b = `${f[i - 1] as string} ${a}`;
			out.set(b, (out.get(b) ?? 0) + 1);
		}
	}
	return out;
}

/** A lexicon: gram -> weight (> 0). */
export type TrLexicon = ReadonlyMap<string, number>;

export interface TrSpan {
	/** UTF-16 offsets into the paragraph text: from the first token's start to the last token's end. */
	start: number;
	end: number;
	gram: string;
	weight: number;
	/** Occurrence number of this gram in the paragraph (1-based). */
	occurrence: number;
	/** Always true: every occurrence contributes to K_p (log1p compression, no cap). Kept for the span consumers. */
	counted: boolean;
	/** This occurrence's share of K_p: weight × (log1p(occurrence) - log1p(occurrence - 1)); the shares of a gram sum to its term. */
	marginal: number;
}

export interface TrParagraphScore {
	/** K_p = Σ weight × log1p(count). */
	k: number;
	/** Every matched occurrence in text order (counted or not), for evidence. */
	spans: TrSpan[];
}

/**
 * Score one paragraph against a lexicon: K_p and the matched spans. Equivalent to Σ weight × log1p(count) over `trGrams`
 * (the dump's per-paragraph counts), by construction: both read the same tokens and adjacency.
 */
export function trScoreParagraph(paragraphText: string, lexicon: TrLexicon): TrParagraphScore {
	const t = trTokenise(paragraphText);
	const f = t.forms;
	const seen = new Map<string, number>();
	const spans: TrSpan[] = [];
	const hit = (gram: string, start: number, end: number): void => {
		const w = lexicon.get(gram);
		if (w === undefined) return;
		const n = (seen.get(gram) ?? 0) + 1;
		seen.set(gram, n);
		spans.push({
			start,
			end,
			gram,
			weight: w,
			occurrence: n,
			counted: true,
			marginal: w * (Math.log1p(n) - Math.log1p(n - 1)),
		});
	};
	for (let i = 0; i < f.length; i++) {
		const a = f[i] as string;
		if (i > 0 && t.adj[i] === 1) {
			// bigram ends at token i; emit in text order after the unigram of token i-1 and before the unigram of token i
			hit(`${f[i - 1] as string} ${a}`, t.starts[i - 1] as number, t.ends[i] as number);
		}
		hit(a, t.starts[i] as number, t.ends[i] as number);
	}
	spans.sort((x, y) => x.start - y.start || x.end - y.end);
	// K_p from the final counts (not by summing marginals), so it equals Σ_g w_g × log1p(count) to the last bit
	let k = 0;
	for (const [gram, n] of seen) k += (lexicon.get(gram) as number) * Math.log1p(n);
	return { k, spans };
}

const EXCL_TOKENS: ReadonlySet<string> = new Set(exclusionData.tokens);
const EXCL_GRAMS: ReadonlySet<string> = new Set(exclusionData.grams);

/** The exclusion-list version, recorded in the lexicon's provenance. */
export const TR_EXCLUSIONS_VERSION: string = exclusionData.version;

/** True for an excluded token (British/American spelling variants, archaic forms). */
export function isExcludedToken(token: string): boolean {
	return EXCL_TOKENS.has(token);
}

/** True when the gram, or any one of its tokens, is on the exclusion list (T2-DESIGN §4.3). */
export function isExcludedGram(gram: string): boolean {
	if (EXCL_GRAMS.has(gram)) return true;
	for (const tok of gram.split(' ')) if (EXCL_TOKENS.has(tok)) return true;
	return false;
}
